import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";

//共通コンポーネント
import { Header, MainColorButton, Loading } from "../../components";
import { bottomButton } from "../../common/styles";
//画面パス
import { CUSTOMER_RESULT, ERROR } from "../../routes/path";
//api
import {
  getMedicalQuestionnaire,
  setMedicalQuestionnaireAnswer,
} from "../../server/axios";
//子供コンポーネント
import {
  QuestionItemOne,
  QuestionItemTwoAndThree,
} from "./items/questionTypeItems";

/**
 * 問診画面
 * @returns
 */
const MedicalQuestionnaires = () => {
  const [endY, setEndY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      !location.state ||
      !location.state.customerId ||
      !location.state.siteId
    ) {
      navigate(ERROR);
    }
  }, []);
  const { siteId, customerId, familyId, gender, birthday, destination } =
    location.state;
  //問診質問
  const [questions, setQuestions] = useState([]);
  //問診回答
  const [resAnswers, setResAnswers] = useState([]);
  //問診質問答え配列
  const [itemAnswers, setItemAnswers] = useState([]);
  //問診質問番号
  const [index, setIndex] = useState(0);
  const [answerYesNo, setAnswerYesNo] = useState(null);
  const [answerYesNoUpdateTime, setAnswerYesNoUpdateTime] = useState("");

  //Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);
  const updateRenderRef = useRef(false);
  //初回render反応しないように
  const RENDER_TIME = process.env.NODE_ENV === "development" ? 1 : 0;
  const firstRenderRef = useRef(0);

  const onChangeItemAnswers = (answer) => {
    if (updateRenderRef.current === false) {
      updateRenderRef.current = true;
    }
    setItemAnswers(answer);
  };

  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      firstRenderRef.current === 0
    ) {
      firstRenderRef.current = firstRenderRef.current + 1;
      return;
    }

    const setSavedItemAnswers = (savedAnswers, q) => {
      const thisAnswers = savedAnswers.find(
        (a) => a.questionNo === q[index].questionNo
      );
      if (thisAnswers) {
        setAnswerYesNoUpdateTime(thisAnswers.updateDatetime);
        if (
          thisAnswers.answerYesNo === true ||
          thisAnswers.answerYesNo === false
        ) {
          setAnswerYesNo(thisAnswers.answerYesNo);
        }
        if (thisAnswers.itemAnswers) {
          setItemAnswers(
            thisAnswers.itemAnswers.map((i) => ({
              ...i,
              itemNo: i.itemNo?.toString(),
              deleteFlg: false,
            }))
          );
        }
      }
    };

    if (index === 0) {
      setLoadingOpen(true);
      getMedicalQuestionnaire({
        customerId,
        siteId,
        familyId,
        gender,
        birthday,
      }).then((res) => {
        setQuestions(res.data.questions);
        if (res.data.questions && res.data.questions.length > 0) {
          setResAnswers(res.data.answers || []);
          setSavedItemAnswers(res.data.answers, res.data.questions);
        }
        setLoadingOpen(false);
      });
    } else {
      setSavedItemAnswers(resAnswers, questions);
    }
  }, [index]);

  useEffect(() => {
    if (firstRenderRef.current > RENDER_TIME) {
      if (
        questions[index] &&
        ((questions[index].questionFormat === "2" && answerYesNo === true) ||
          (questions[index].questionFormat === "3" && answerYesNo === false))
      ) {
        setItemAnswers([]);
      }
    }
    firstRenderRef.current = firstRenderRef.current + 1;
  }, [answerYesNo]);

  const mainWork = () => {
    const gotoTheNextOne = () => {
      if (index === questions.length - 1) {
        navigate(CUSTOMER_RESULT, {
          state: {
            header: "問診",
            message: `「${
              location.state.fullName || location.state.name
            }」様の問診を登録しました。`,
            destination,
          },
        });
      } else {
        updateRenderRef.current = false;
        setAnswerYesNoUpdateTime("");
        setAnswerYesNo(null);
        setItemAnswers([]);
        setIndex(index + 1);
        const scrollArea = document.getElementById("scrollArea");
        scrollArea.scrollTop = 0;
      }
    };

    const isLastOneQuestion = index === questions.length - 1;
    if (updateRenderRef.current || isLastOneQuestion) {
      setLoadingOpen(true);
      setMedicalQuestionnaireAnswer({
        customerId,
        siteId,
        familyId,
        questionNo: updateRenderRef.current
          ? questions[index].questionNo
          : undefined,
        answerYesNo: answerYesNo === null ? undefined : answerYesNo,
        itemAnswers,
        //最後の質問かどうか
        isLastOneQuestion,
        //登録後編集できる
        updateDatetime:
          answerYesNoUpdateTime === "" ? undefined : answerYesNoUpdateTime,
      }).then((r) => {
        if (r.data.result === -1) {
          setLoadingOpen(false);
          //排他エラー処理
          navigate(ERROR);
          return;
        }
        gotoTheNextOne();
        setLoadingOpen(false);
      });
    } else {
      gotoTheNextOne();
    }
  };

  const getMainButtonDisabled = () => {
    if (questions[index].questionFormat === "1") {
      return itemAnswers && itemAnswers.length === 0;
    } else if (questions[index].questionFormat === "4") {
      return false;
    } else {
      return answerYesNo === null;
    }
  };

  return (
    <div
      onTouchStartCapture={(e) => {
        setEndY(0);
      }}
      onTouchMoveCapture={(e) => {
        setEndY(e.touches[0].clientY);
      }}
      onTouchEndCapture={(e) => {
        if (endY !== 0) {
          e.stopPropagation();
        }
      }}
    >
      <Loading open={loadingOpen} />
      {questions.length > 0 && (
        <>
          <Header
            title="問診"
            subtitle={`（${index + 1}/${questions.length}）`}
          />
          <Box
            id="scrollArea"
            sx={{ height: "76vh", overflowY: "scroll" }}
            marginTop="9vh"
            width="95%"
            marginLeft="auto"
            marginRight="auto"
          >
            <Stack spacing="5%">
              <Stack direction="row" spacing="15%">
                <Typography>
                  <strong>問診者</strong>
                </Typography>
                <Box sx={{ border: "1px solid", paddingX: "6%" }}>
                  {location.state.fullName || location.state.name}&emsp;様
                </Box>
              </Stack>
              <Stack direction="row">
                <Box width="20%">
                  <Typography>
                    <strong>問{index + 1}</strong>
                  </Typography>
                </Box>
                <Typography whiteSpace="pre-wrap">
                  {questions[index].questionText}
                </Typography>
              </Stack>
              <Box sx={{ marginLeft: "15%" }}>
                {["1", "4"].includes(questions[index].questionFormat) && (
                  <RadioGroup
                    onChange={(e) => {
                      if (updateRenderRef.current === false) {
                        updateRenderRef.current = true;
                      }
                      setItemAnswers([{ itemNo: e.target.value }]);
                    }}
                  >
                    <Box sx={{ height: "100%" }}>
                      {questions[index].questionType === "1" && (
                        <>
                          {questions[index].medicalQuestionnaireItem.map(
                            (item, index) => {
                              if (item.itemTextAnswerType === "1") {
                                return (
                                  <FormControlLabel
                                    sx={{ marginLeft: "16%" }}
                                    key={index}
                                    value={item.itemNo}
                                    checked={
                                      itemAnswers && itemAnswers.length > 0
                                        ? itemAnswers[0].itemNo?.toString() ===
                                          item.itemNo?.toString()
                                        : undefined
                                    }
                                    control={<Radio />}
                                    label={item.itemTitle}
                                  />
                                );
                              } else {
                                return <></>;
                              }
                            }
                          )}
                        </>
                      )}
                      {["2", "3"].includes(questions[index].questionType) && (
                        <QuestionItemTwoAndThree
                          answerYesNo={true}
                          questionnaireItems={
                            questions[index].medicalQuestionnaireItem
                          }
                          itemAnswers={itemAnswers}
                          setItemAnswers={onChangeItemAnswers}
                        />
                      )}
                    </Box>
                  </RadioGroup>
                )}
                {questions[index].questionFormat === "2" && (
                  <>
                    <RadioGroup
                      sx={{ marginLeft: "16%" }}
                      value={answerYesNo}
                      onChange={(e) => {
                        if (updateRenderRef.current === false) {
                          updateRenderRef.current = true;
                        }
                        setAnswerYesNo(e.target.value === "true");
                      }}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        checked={answerYesNo?.toString() === "true"}
                        label={questions[index].questionYesTitle}
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        checked={answerYesNo?.toString() === "false"}
                        label={questions[index].questionNoTitle}
                      />
                    </RadioGroup>
                    <Box sx={{ height: "100%" }}>
                      {["2", "3"].includes(questions[index].questionType) && (
                        <QuestionItemTwoAndThree
                          answerYesNo={
                            answerYesNo === null ? false : !answerYesNo
                          }
                          questionnaireItems={
                            questions[index].medicalQuestionnaireItem
                          }
                          itemAnswers={itemAnswers}
                          setItemAnswers={onChangeItemAnswers}
                        />
                      )}
                    </Box>
                  </>
                )}
                {questions[index].questionFormat === "3" && (
                  <>
                    <RadioGroup
                      sx={{ marginLeft: "16%" }}
                      value={answerYesNo}
                      onChange={(e) => {
                        if (updateRenderRef.current === false) {
                          updateRenderRef.current = true;
                        }
                        setAnswerYesNo(e.target.value === "true");
                      }}
                    >
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        checked={answerYesNo?.toString() === "false"}
                        label={questions[index].questionNoTitle}
                      />
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        checked={answerYesNo?.toString() === "true"}
                        label={questions[index].questionYesTitle}
                      />
                    </RadioGroup>
                    <Box sx={{ height: "100%" }}>
                      {questions[index].questionType === "1" && (
                        <QuestionItemOne
                          answerYesNo={answerYesNo}
                          questionnaireItems={
                            questions[index].medicalQuestionnaireItem
                          }
                          itemAnswers={itemAnswers}
                          setItemAnswers={onChangeItemAnswers}
                        />
                      )}
                      {["2", "3"].includes(questions[index].questionType) && (
                        <QuestionItemTwoAndThree
                          answerYesNo={answerYesNo}
                          questionnaireItems={
                            questions[index].medicalQuestionnaireItem
                          }
                          itemAnswers={itemAnswers}
                          setItemAnswers={onChangeItemAnswers}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Stack>
          </Box>
          <MainColorButton
            label={index === questions.length - 1 ? "登録" : "次へ"}
            onClick={mainWork}
            style={bottomButton}
            disabled={getMainButtonDisabled()}
          />
        </>
      )}
    </div>
  );
};

export default MedicalQuestionnaires;
